import { getAuth } from 'firebase/auth'
import {
  collection,
  doc,
  getDocFromServer,
  getDocsFromServer,
  setDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db, functions } from '@/firebase'
import { httpsCallable } from 'firebase/functions'
import { useAuthStore } from '@/store/authStore'
import { isWorkspaceRotatorEmail } from '@/utils/workspaceRoleRotator'
import { matchesSmokeClaims } from './authContext.js'
import { rotateDemoRoleInSession } from './roleRotate.js'

const RECOVERY_KEY = 'cmv-smoke-profile-recovery'
export const DISTRICT_SMOKE_PROFILES = [
  { role: 'district_admin', allSchools: false, label: 'District Admin' },
  { role: 'district_coordinator', allSchools: false, label: 'District Coordinator' },
  { role: 'case_manager', allSchools: true, label: 'District Case Manager' },
  { role: 'case_manager_full', allSchools: true, label: 'District Case Manager (All)' },
  { role: 'speech_case_manager', allSchools: true, label: 'District Speech Case Manager' }
]

export function pendingSmokeProfile() {
  const raw = sessionStorage.getItem(RECOVERY_KEY)
  return raw ? JSON.parse(raw) : null
}

async function verifyProfile(profile) {
  const user = getAuth().currentUser
  if (!user || user.uid !== profile.uid)
    throw new Error('Signed-in account changed during smoke check')
  const token = await user.getIdTokenResult(true)
  if (!matchesSmokeClaims(token.claims, profile.role, profile))
    throw new Error('JWT role or school scope does not match the requested smoke profile')
}

export async function restoreSmokeProfile() {
  const profile = pendingSmokeProfile()
  if (!profile) return 'No profile restoration pending'
  return restoreProfile(profile)
}

async function restoreProfile(profile) {
  if (getAuth().currentUser?.uid !== profile.uid)
    throw new Error('Sign in with the account that started this smoke check to restore its profile')
  await rotateDemoRoleInSession(profile.role, { allSchools: profile.allSchools })
  await verifyProfile(profile)
  sessionStorage.removeItem(RECOVERY_KEY)
  return 'Starting role, allSchools flag and unchanged school assignments verified'
}

export function fixtureSessionBlockReason(ctx) {
  if (import.meta.env.VITE_FIREBASE_PROJECT_ID !== 'casemangervue') return 'Hosted dev only'
  if (!isWorkspaceRotatorEmail(ctx.user?.email))
    return 'Use the allowlisted demo login for disposable fixtures and district profile rotation'
  if (pendingSmokeProfile())
    return 'Restore the pending smoke profile before starting another check'
  return null
}

/** Isolated campuses and students; never changes the selected school or its import history. */
export function createFixtureSession(ctx) {
  const schools = [0, 1].map(() => doc(collection(db, 'schools')))
  const students = []
  const evaluations = []
  let started = false
  let baseline
  return {
    schools,
    students,
    async begin() {
      const profile = await getDocFromServer(doc(db, 'users', ctx.user.uid))
      if (!profile.exists()) throw new Error('Demo profile is missing')
      const data = profile.data()
      baseline = {
        uid: ctx.user.uid,
        role: data.role,
        allSchools: data.allSchools ?? null,
        schoolIds: Array.isArray(data.schoolIds) ? data.schoolIds : null
      }
      if (
        baseline.allSchools === true &&
        !DISTRICT_SMOKE_PROFILES.some((p) => p.role === baseline.role && p.allSchools)
      )
        throw new Error(
          'Remove the stale allSchools flag from this non-case-manager demo profile first'
        )
      await verifyProfile(baseline)
      sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(ctx.recoveryProfile || baseline))
      started = true
      await this.as(DISTRICT_SMOKE_PROFILES[0])
      await httpsCallable(
        functions,
        'prepareSmokeSchools'
      )({
        runId: ctx.runId,
        schoolIds: schools.map((school) => school.id)
      })
      return 'Two disposable campuses; existing schools and assignments unchanged'
    },
    async as(profile) {
      if (getAuth().currentUser?.uid !== ctx.user.uid) throw new Error('Signed-in account changed')
      await rotateDemoRoleInSession(profile.role, { allSchools: profile.allSchools })
      await verifyProfile({ ...profile, uid: ctx.user.uid, schoolIds: baseline.schoolIds })
      return `${profile.label || profile.role}: real JWT role and allSchools verified`
    },
    async student(schoolIndex, data) {
      const ref = doc(collection(db, 'students'))
      // Track before the request: a lost response may still have committed.
      students.push(ref)
      await setDoc(ref, {
        ...data,
        schoolId: schools[schoolIndex].id,
        __smokeTest: true,
        __smokeRunId: ctx.runId
      })
      return ref
    },
    async evaluation(data) {
      const ref = doc(collection(db, 'studentEvaluations'))
      evaluations.push(ref)
      await setDoc(ref, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        __smokeTest: true,
        __smokeRunId: ctx.runId
      })
      return ref
    },
    async cleanup() {
      if (!started) return 'No fixtures created'
      await this.as(DISTRICT_SMOKE_PROFILES[0])
      const evalResults = await Promise.allSettled(evaluations.map((ref) => deleteDoc(ref)))
      const results = await Promise.allSettled(
        students.map(async (ref) => {
          const history = await getDocsFromServer(
            collection(db, 'students', ref.id, 'changeHistory')
          )
          for (const entry of history.docs) await deleteDoc(entry.ref)
          await deleteDoc(ref)
        })
      )
      if ([...evalResults, ...results].some((r) => r.status === 'rejected'))
        throw new Error('Some disposable students could not be removed; run Clean up leftovers')
      return 'Students removed; tagged school shells are removed by the runner’s Admin SDK cleanup'
    },
    async restore() {
      if (!started) return 'No profile change'
      const detail = await restoreProfile(baseline)
      // Keep the runner context aligned for subsequent scenarios.
      ctx.roleClaim = baseline.role
      ctx.tokenSchoolIds = baseline.schoolIds
      useAuthStore().mergeCurrentUser({
        role: baseline.role,
        allSchools: baseline.allSchools === true
      })
      return detail
    }
  }
}

/** Cleanup and restoration run independently even after setup or a probe fails. */
export async function withFixtureSession(ctx, step, io, run) {
  let failure
  try {
    await step('prepare disposable campuses and verify setup JWT', () => io.begin())
    await run()
  } catch (error) {
    failure = error
  }
  try {
    await step('clean up disposable students', () => io.cleanup())
  } catch (error) {
    failure ||= error
  }
  try {
    await step('restore starting role and school scope', () => io.restore())
  } catch (error) {
    ctx.authInvalid = true
    failure = error
  }
  if (failure) throw failure
}
